/**
 * Drawer flotante del COPILOTO (el nuevo, de acciones — edge fn crm-copilot).
 *
 * Es el modal que pidió el founder: el botón flotante abre acá el chat con el agente
 * principal (las 43 acciones: subir venta, editar precio, armar el agente de ventas,
 * cargar conocimiento, etc.) SIN salir de la pantalla en la que está.
 *
 * Reusa `ChatInterface` de CrmCopilotPage en modo `embedded` — misma lógica/hooks
 * (crm-copilot), no una copia. NO es el asistente viejo de CRM (ai-copilot).
 */
import { lazy, Suspense, useState } from 'react';
import { Spinner } from '@/lib/polaris';
import { AppIconButton } from '@/components/admin/AppIconButton';
import { XIcon } from '@/lib/polaris-icons';

// Lazy: el chat del copiloto (y sus deps) solo se cargan al abrir el drawer, no en el
// bundle principal. Reusa el MISMO ChatInterface de la página (edge fn crm-copilot).
const ChatInterface = lazy(() =>
  import('@/pages/crm/CrmCopilotPage').then((m) => ({ default: m.ChatInterface })),
);

export function CopilotDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  // Hilo local del drawer (arranca en chat nuevo). No toca la URL.
  const [threadId, setThreadId] = useState<string | null>(null);
  if (!open) return null;

  return (
    <>
      {/* Fondo para cerrar al tocar afuera */}
      <div
        onClick={onClose}
        aria-hidden="true"
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.32)', zIndex: 99998 }}
      />
      {/* Panel derecho */}
      <div
        role="dialog"
        aria-label="Copiloto"
        style={{
          position: 'fixed', top: 0, right: 0, bottom: 0,
          width: 'min(480px, 100vw)', background: 'var(--p-color-bg, #fff)',
          zIndex: 99999, boxShadow: '-4px 0 24px rgba(0,0,0,0.18)',
          display: 'flex', flexDirection: 'column',
          animation: 'copilotDrawerIn 200ms ease',
        }}
      >
        <style>{'@keyframes copilotDrawerIn{from{transform:translateX(100%)}to{transform:translateX(0)}}'}</style>
        <div
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '12px 16px', color: '#fff',
            background: 'linear-gradient(135deg, #2563EB, #4F46E5)', flex: '0 0 auto',
          }}
        >
          <span style={{ fontWeight: 600, fontSize: 15, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 18 }}>✨</span> Copiloto
          </span>
          <AppIconButton icon={XIcon} accessibilityLabel="Cerrar el copiloto" onClick={onClose} />
        </div>
        <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
          <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', paddingTop: 40 }}><Spinner size="small" /></div>}>
            <ChatInterface
              embedded
              activeThreadId={threadId}
              onThreadChange={(t) => setThreadId(t === 'new' ? null : t)}
            />
          </Suspense>
        </div>
      </div>
    </>
  );
}
